import { Typography } from '@mui/material'
import type React from 'react'
import { useContext, useState } from 'react'
import styled from 'styled-components'

import PaperedGridItem from '@/components/UI/PaperedGridItem'
import ctx from '@/context/AppContext'
import { graphQLQuery } from '@/hooks/hooks'
import QUERIES from '@/lib/graphql'
import type { UserData } from '@/types/User'

interface Props {
  userData: UserData
  onChangeCustomerId: (customerId: string) => void
}

const SelectInput = styled.select`
  padding: 10px;
  border-radius: 5px;
  outline: 0;

  option {
    padding: 5px;
    background: transparent;
  }
`

const AccountSettings: React.FC<Props> = ({ userData, onChangeCustomerId }) => {
  const [customerSwitchStatus, setCustomerSwitchStatus] = useState<
    'Error' | 'Success' | 'Loading' | null
  >(null)

  const { personId, token, customerId } = useContext(ctx)

  async function handleCustomerChange(customerId: string) {
    if (!userData || !userData.workspaceSettings || !customerId) return
    setCustomerSwitchStatus('Loading')

    graphQLQuery({
      query: QUERIES.switchCustomer,
      variables: {
        customerId,
        personId,
      },
      customHeaders: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(resData => {
        return resData.json()
      })
      .then(
        ({
          data: {
            switchCustomer: { status, message, body },
          },
        }) => {
          switch (status) {
            case 200:
              onChangeCustomerId(body.workspaceSettings.lastAccessedCustomer.id)
              setCustomerSwitchStatus('Success')
              break
            default:
              console.log(message)
              setCustomerSwitchStatus('Error')
          }
        },
      )
  }

  return (
    <>
      {userData.workspaceSettings && userData.workspaceSettings?.allowedCustomers && (
        <PaperedGridItem item xs={12}>
          <Typography component="h2" variant="h6">
            Which company would you like to access?
          </Typography>
          <SelectInput
            key={customerId}
            disabled={customerSwitchStatus === 'Loading'}
            defaultValue={customerId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleCustomerChange(e.target.value)
            }
          >
            {userData.workspaceSettings &&
              userData.workspaceSettings?.allowedCustomers.map((customer, index) => (
                <option key={index} value={customer.id}>
                  {customer.name}
                </option>
              ))}
          </SelectInput>
          {customerSwitchStatus === 'Error' && (
            <p>Error switching customer. Please try again later.</p>
          )}
          {customerSwitchStatus === 'Success' && (
            <p>Successfully switched to the new customer.</p>
          )}
          {customerSwitchStatus === 'Loading' && <p>Loading new customer...</p>}
        </PaperedGridItem>
      )}
    </>
  )
}

export default AccountSettings
